/**
 * Tolerancias de inspección ID-25 / ID-26 (Plastyverg) a partir del artículo seleccionado.
 * Aplica `hasAlertNumberRange` + `invertAlertNumberRange` como OT Premium en cliente.
 */

export type ArticuloToleranceBoundEntry = {
  value?: number;
  min?: string | null;
  max?: string | null;
};

export type ArticuloToleranceBoundsProfile = {
  ancho?: ArticuloToleranceBoundEntry;
  espesor?: ArticuloToleranceBoundEntry;
  peso?: ArticuloToleranceBoundEntry;
  cling?: ArticuloToleranceBoundEntry;
  punz?: ArticuloToleranceBoundEntry;
  elongacion?: ArticuloToleranceBoundEntry;
};

export type PlastyvergArticuloVariant = 'ID-25' | 'ID-26';

const LABEL_MATCH_ESPESOR = ['ESPESOR'];
const LABEL_MATCH_PESO = ['PESO'];
const LABEL_MATCH_ANCHO = ['ANCHO'];
const LABEL_EXCLUDE_ANCHO = ['ANCHOS', 'N° DE ANCHO', 'N DE ANCHO', 'FUELLE', 'FUELLES'];
const LABEL_MATCH_CLING = ['CLING'];
const LABEL_MATCH_PUNZ = ['PUNZ'];
const LABEL_MATCH_ELONG = ['ELONG'];

const DESC_ARTICULO_DIMENSIONS_REGEX = /(\d+(?:[.,]\d+)?)\s*\*\s*(\d+(?:[.,]\d+)?)(?:\s*\*\s*(\d+(?:[.,]\d+)?))?/;

const TOLERANCE_ID25 = {ancho: 0.2, espesor: 2, peso: 3};
const TOLERANCE_ID26 = {ancho: 0.2, espesor: 2, peso: 3};

function normalizeLabel(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function labelIncludesAny(label: string, needles: readonly string[]): boolean {
  return needles.some((needle) => label.includes(normalizeLabel(needle)));
}

function labelExcludesAny(label: string, needles: readonly string[]): boolean {
  return needles.some((needle) => label.includes(normalizeLabel(needle)));
}

function parseNumericValue(value: unknown): number | null {
  if (value == null || value === '') return null;
  const numeric = Number(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : null;
}

function formatBoundNumber(value: number): string {
  return String(Number(value.toFixed(6)));
}

function boundFromValueAndDelta(value: number, delta: number): ArticuloToleranceBoundEntry {
  return {
    value,
    min: formatBoundNumber(value - delta),
    max: formatBoundNumber(value + delta),
  };
}

function boundFromValueAndPercent(value: number, percent: number): ArticuloToleranceBoundEntry {
  const delta = value * (percent / 100);
  return boundFromValueAndDelta(value, delta);
}

function descripcionContainsSilo(descripcion: unknown): boolean {
  return String(descripcion ?? '')
    .toUpperCase()
    .includes('SILO');
}

export function parseArticuloDescripcionDimensiones(descripcion: unknown): {
  ancho?: number;
  espesor?: number;
} {
  const text = String(descripcion ?? '');
  const match = text.match(DESC_ARTICULO_DIMENSIONS_REGEX);
  if (!match) return {};

  const first = parseNumericValue(match[1]);
  const second = parseNumericValue(match[2]);
  const third = parseNumericValue(match[3]);

  const ancho = first ?? undefined;
  const espesor = third ?? second ?? undefined;

  return {
    ...(ancho != null ? {ancho} : {}),
    ...(espesor != null ? {espesor} : {}),
  };
}

export function parseCatalogRangeString(value: unknown): ArticuloToleranceBoundEntry | null {
  const text = String(value ?? '')
    .replace(/,/g, '.')
    .trim();
  if (!text) return null;

  const rangeMatch = text.match(/(\d+(?:\.\d+)?)\s*[-–—]\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return {min: rangeMatch[1], max: rangeMatch[2]};
  }

  const gteMatch = text.match(/>\s*=\s*(\d+(?:\.\d+)?)/);
  if (gteMatch) {
    return {min: gteMatch[1], max: null};
  }

  const gtMatch = text.match(/>\s*(\d+(?:\.\d+)?)/);
  if (gtMatch) {
    return {min: gtMatch[1], max: null};
  }

  const lteMatch = text.match(/<\s*=\s*(\d+(?:\.\d+)?)/);
  if (lteMatch) {
    return {min: null, max: lteMatch[1]};
  }

  const ltMatch = text.match(/<\s*(\d+(?:\.\d+)?)/);
  if (ltMatch) {
    return {min: null, max: ltMatch[1]};
  }

  return null;
}

export function resolvePlastyvergArticuloVariant(titulo?: string | null): PlastyvergArticuloVariant | null {
  const normalized = normalizeLabel(titulo);
  if (!normalized) return null;
  if (normalized.includes('ID-25')) return 'ID-25';
  if (normalized.includes('ID-26')) return 'ID-26';
  return null;
}

export function resolvePlastyvergArticuloTitulo(source: any): string {
  return (
    source?.tituloTarea ||
    source?.modeloTarea?.titulo ||
    source?.modeloTarea?.label ||
    source?.titulo ||
    source?.label ||
    source?.record?.titulo ||
    ''
  );
}

export function titlesConflictId25AndId26(parentTitle?: string | null, childTitle?: string | null) {
  const parentVariant = resolvePlastyvergArticuloVariant(parentTitle);
  const childVariant = resolvePlastyvergArticuloVariant(childTitle);
  return Boolean(parentVariant && childVariant && parentVariant !== childVariant);
}

export function findArticuloAnswerFromLista(lista: any[]): any | null {
  const item = (lista || []).find((entry) => entry?.tipo === 'pregunta' && entry?.tipoRespuesta === 'articulo');
  return item?.articulo || null;
}

export function buildArticuloToleranceBoundsProfile(
  articulo: any,
  variant: PlastyvergArticuloVariant | null,
): ArticuloToleranceBoundsProfile {
  if (!articulo || !variant) return {};

  const profile: ArticuloToleranceBoundsProfile = {};
  const {ancho, espesor} = parseArticuloDescripcionDimensiones(articulo.descripcion);
  const peso = parseNumericValue(articulo.kgBruto);

  if (ancho != null) {
    profile.ancho = boundFromValueAndDelta(ancho, variant === 'ID-25' ? TOLERANCE_ID25.ancho : TOLERANCE_ID26.ancho);
  }

  if (espesor != null) {
    profile.espesor = boundFromValueAndDelta(espesor, variant === 'ID-25' ? TOLERANCE_ID25.espesor : TOLERANCE_ID26.espesor);
  }

  if (peso != null) {
    profile.peso = boundFromValueAndPercent(peso, variant === 'ID-25' ? TOLERANCE_ID25.peso : TOLERANCE_ID26.peso);
  }

  const cling = parseCatalogRangeString(articulo.rangoClingG);
  if (cling) profile.cling = cling;

  const punz = parseCatalogRangeString(articulo.punzLabMm);
  if (punz) profile.punz = punz;

  const elongacion =
    variant === 'ID-25' && descripcionContainsSilo(articulo.descripcion)
      ? parseCatalogRangeString('>= 300 %')
      : parseCatalogRangeString(articulo.elongacionMin);
  if (elongacion) profile.elongacion = elongacion;

  return profile;
}

function resolveToleranceEntryForQuestion(
  label: string,
  profile: ArticuloToleranceBoundsProfile,
): ArticuloToleranceBoundEntry | undefined {
  if (labelIncludesAny(label, LABEL_MATCH_ESPESOR)) return profile.espesor;
  if (labelIncludesAny(label, LABEL_MATCH_PESO)) return profile.peso;
  if (labelIncludesAny(label, LABEL_MATCH_CLING)) return profile.cling;
  if (labelIncludesAny(label, LABEL_MATCH_PUNZ)) return profile.punz;
  if (labelIncludesAny(label, LABEL_MATCH_ELONG)) return profile.elongacion;
  if (labelIncludesAny(label, LABEL_MATCH_ANCHO) && !labelExcludesAny(label, LABEL_EXCLUDE_ANCHO)) {
    return profile.ancho;
  }
  return undefined;
}

function setAlertNumberRangeFromEntry(item: any, entry: ArticuloToleranceBoundEntry) {
  const min = entry.min != null && entry.min !== '' ? entry.min : null;
  const max = entry.max != null && entry.max !== '' ? entry.max : null;
  if (min == null && max == null) return;

  item.hasAlertNumberRange = true;
  item.alertNumberRange = [min, max];
  item.invertAlertNumberRange = true;
  item.articuloAutoAlert = true;
}

function clearArticuloAutoAlert(item: any) {
  if (!item?.articuloAutoAlert) return item;
  const clone = {...item};
  delete clone.articuloAutoAlert;
  delete clone.hasAlertNumberRange;
  delete clone.alertNumberRange;
  delete clone.invertAlertNumberRange;
  return clone;
}

function applyProfileToPregunta(item: any, profile: ArticuloToleranceBoundsProfile | null): any {
  if (item?.tipo !== 'pregunta' || item?.tipoRespuesta !== 'numberField') {
    return item;
  }

  const cleared = clearArticuloAutoAlert(item);
  if (!profile || !Object.keys(profile).length) {
    return cleared;
  }

  const label = normalizeLabel(cleared?.pregunta?.label || cleared?.pregunta?.titulo || '');
  if (!label) return cleared;

  const entry = resolveToleranceEntryForQuestion(label, profile);
  if (!entry) return cleared;

  const clone = {...cleared};
  setAlertNumberRangeFromEntry(clone, entry);
  return clone;
}

export function applyArticuloToleranceBoundsToLista(
  lista: any[],
  profile: ArticuloToleranceBoundsProfile | null | undefined,
): any[] {
  return (lista || []).map((item) => applyProfileToPregunta(item, profile || null));
}

export function applyArticuloToleranceBoundsForTask(lista: any[], articulo: any, titulo?: string | null): any[] {
  const variant = resolvePlastyvergArticuloVariant(titulo);
  const profile = variant ? buildArticuloToleranceBoundsProfile(articulo, variant) : null;
  return applyArticuloToleranceBoundsToLista(lista, profile);
}

export function applyArticuloToleranceBoundsToEstructura(estructura: any[], articulo: any, titulo?: string | null): any[] {
  const variant = resolvePlastyvergArticuloVariant(titulo);
  const profile = variant ? buildArticuloToleranceBoundsProfile(articulo, variant) : null;

  return (estructura || []).map((entry) => {
    if (entry?.tipo !== 'grupo') return entry;
    return {
      ...entry,
      subsecciones: (entry.subsecciones || []).map((sub) => ({
        ...sub,
        preguntas: applyArticuloToleranceBoundsToLista(sub.preguntas || [], profile),
      })),
    };
  });
}

export function applyArticuloToleranceBoundsFromLista(lista: any[], titulo?: string | null): any[] {
  return applyArticuloToleranceBoundsForTask(lista, findArticuloAnswerFromLista(lista), titulo);
}
